import { Injectable } from '@angular/core';

export enum PollingTrigger {
  MANUAL = 'MANUAL',
  TIMER = 'TIMER',
  RESUME = 'RESUME',
}

export interface PollingOptions {
  intervalMs: number;
  /** Deve rejeitar em falha. NÃO deve chamar treatError: quem trata erro é o caminho manual. */
  task: (trigger: PollingTrigger) => Promise<void>;
  /** Se retornar false, o ciclo é pulado (ex.: pedido já em status final). */
  shouldRun?: () => boolean;
  /** Se a tela deve pular os ciclos enquanto a aba está oculta. Default true. */
  pauseWhenHidden?: boolean;
  /** Teto de duração de um ciclo, para um request pendurado não bloquear os próximos. Default 30000ms. */
  taskTimeoutMs?: number;
  /** Avisa a tela que os dados estão desatualizados. */
  onStaleChange?: (stale: boolean, error?: any) => void;
}

export interface PollingHandle {
  start(): void;
  stop(): void;
  runNow(trigger?: PollingTrigger): Promise<void>;
  /** Informa que uma atualização feita FORA do ciclo (pull-to-refresh, botão) teve sucesso. */
  markFresh(): void;
  destroy(): void;
  isStale(): boolean;
}

const DEFAULT_TASK_TIMEOUT_MS: number = 30000;
const STALE_AFTER_FAILURES: number = 2;

/**
 * Polling simples: um intervalo que não morre, mais um refresh ao voltar para a aba.
 *
 * A regra que sustenta tudo: o agendamento NUNCA depende do resultado de um request.
 * O setInterval segue disparando aconteça o que acontecer com a task — se um request ficar
 * pendurado (aba congelada em background), os ticks seguintes apenas o ignoram e a vida continua.
 */
class PollingHandleImpl implements PollingHandle {
  private interval: any;
  private inFlight: boolean;
  private failures: number = 0;
  private lastSuccessAt: number = null;
  private stale: boolean;
  private destroyed: boolean;

  private onVisibilityChange: any;

  constructor(private options: PollingOptions) {
    this.onVisibilityChange = () => {
      if (!document.hidden) {
        this.runNow(PollingTrigger.RESUME);
      }
    };
  }

  start(): void {
    if (this.destroyed) {
      return;
    }
    this.stop();
    this.interval = setInterval(() => {
      this.runNow(PollingTrigger.TIMER);
    }, this.options.intervalMs);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  stop(): void {
    if (this.interval != null) {
      clearInterval(this.interval);
      this.interval = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
  }

  async runNow(trigger: PollingTrigger = PollingTrigger.MANUAL): Promise<void> {
    if (this.destroyed || this.inFlight) {
      return;
    }
    if (this.pauseWhenHidden() && document.hidden) {
      return;
    }
    if (this.options.shouldRun && !this.options.shouldRun()) {
      return;
    }
    // Voltar para a tela não precisa custar uma requisição se o dado ainda está novo:
    // alternar de aba rapidamente não deve refazer o que acabou de ser feito.
    if (trigger === PollingTrigger.RESUME && this.isFresh()) {
      return;
    }
    this.inFlight = true;
    try {
      await this.withTimeout(this.options.task(trigger), this.taskTimeoutMs());
      this.failures = 0;
      this.lastSuccessAt = Date.now();
      this.setStale(false);
    } catch (error) {
      this.failures++;
      // A primeira falha logo após o resume costuma ser a rede ainda subindo
      if (this.failures >= STALE_AFTER_FAILURES) {
        this.setStale(true, error);
      }
      console.error('Falha no polling (tentativa ' + this.failures + ')', error);
    } finally {
      this.inFlight = false;
    }
  }

  /**
   * O caminho manual (pull-to-refresh, botão do aviso) não passa pelo runNow, então sem isto
   * o estado "desatualizado" nunca era limpo: a tela atualizava mas o aviso continuava lá.
   */
  markFresh(): void {
    this.failures = 0;
    this.lastSuccessAt = Date.now();
    this.setStale(false);
  }

  /**
   * Só devolve true quando dá para AFIRMAR que o dado é recente. Em qualquer dúvida — nunca
   * atualizou, ou o relógio do aparelho andou para trás — devolve false para que o retorno
   * à tela atualize, que é o comportamento seguro.
   */
  private isFresh(): boolean {
    if (this.lastSuccessAt == null) {
      return false;
    }
    const elapsedMs: number = Date.now() - this.lastSuccessAt;
    if (elapsedMs < 0) {
      return false;
    }
    return elapsedMs < this.options.intervalMs;
  }

  isStale(): boolean {
    return this.stale;
  }

  private pauseWhenHidden(): boolean {
    return this.options.pauseWhenHidden !== false;
  }

  private taskTimeoutMs(): number {
    return this.options.taskTimeoutMs != null
      ? this.options.taskTimeoutMs
      : DEFAULT_TASK_TIMEOUT_MS;
  }

  /** Garante que o inFlight sempre é liberado, mesmo se o request nunca responder. */
  private withTimeout(promise: Promise<void>, timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer: any = setTimeout(() => {
        reject(new Error('Ciclo de polling excedeu ' + timeoutMs + 'ms'));
      }, timeoutMs);
      promise.then(
        () => {
          clearTimeout(timer);
          resolve();
        },
        (error: any) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  private setStale(stale: boolean, error?: any): void {
    if (this.stale !== stale) {
      this.stale = stale;
      if (this.options.onStaleChange) {
        this.options.onStaleChange(stale, error);
      }
    }
  }
}

@Injectable()
export class PollingService {
  create(options: PollingOptions): PollingHandle {
    return new PollingHandleImpl(options);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/polling.service.ts